import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface GoogleIntegration {
  id: string;
  pta_id: string;
  google_email: string;
  is_enabled: boolean;
  meet_enabled: boolean;
  drive_enabled: boolean;
  connected_at: string;
}

export const useGoogleIntegration = (ptaId: string | null) => {
  const [integration, setIntegration] = useState<GoogleIntegration | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (ptaId) {
      fetchIntegration();
    } else {
      setLoading(false);
    }
  }, [ptaId]);

  const fetchIntegration = async () => {
    if (!ptaId) return;
    
    setLoading(true);
    try {
      // Use secure function to fetch integration without exposing tokens
      const { data, error } = await supabase
        .rpc('get_google_integration_safe', { p_pta_id: ptaId });

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching Google integration:', error);
      }
      
      // Function returns array, get first item
      const integration = Array.isArray(data) && data.length > 0 ? data[0] : null;
      setIntegration(integration);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectGoogle = async (redirectUrl?: string) => {
    if (!ptaId) {
      toast({
        title: 'Error',
        description: 'PTA ID not found',
        variant: 'destructive',
      });
      return;
    }

    setConnecting(true);
    try {
      // Always pass full URL to ensure correct redirect after OAuth
      const fullRedirectUrl = redirectUrl 
        ? (redirectUrl.startsWith('http') ? redirectUrl : `${window.location.origin}${redirectUrl}`)
        : `${window.location.origin}/pta-admin?tab=integrations`;

      const { data, error } = await supabase.functions.invoke('google-auth', {
        body: { ptaId, redirectUrl: fullRedirectUrl },
      });

      if (error) throw error;

      if (data?.authUrl) {
        window.location.href = data.authUrl;
      } else {
        throw new Error('No auth URL returned');
      }
    } catch (error: any) {
      console.error('Error connecting Google:', error);
      toast({
        title: 'Connection failed',
        description: error.message || 'Failed to connect Google Calendar',
        variant: 'destructive',
      });
      setConnecting(false);
    }
  };

  const disconnectGoogle = async () => {
    if (!ptaId) return;

    try {
      const { error } = await supabase.functions.invoke('google-disconnect', {
        body: { ptaId },
      });

      if (error) throw error;

      setIntegration(null);
      toast({
        title: 'Disconnected',
        description: 'Google Calendar has been disconnected',
      });
    } catch (error: any) {
      console.error('Error disconnecting:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to disconnect',
        variant: 'destructive',
      });
    }
  };

  const toggleCalendarEnabled = async (enabled: boolean) => {
    if (!integration?.id) return;

    try {
      const { error } = await supabase
        .from('pta_google_integrations')
        .update({ is_enabled: enabled })
        .eq('id', integration.id);

      if (error) throw error;

      setIntegration({ ...integration, is_enabled: enabled });
      toast({
        title: enabled ? 'Enabled' : 'Disabled',
        description: `Google Calendar sync ${enabled ? 'enabled' : 'disabled'}`,
      });
    } catch (error: any) {
      console.error('Error toggling calendar:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const toggleMeetEnabled = async (enabled: boolean) => {
    if (!integration?.id) return;

    try {
      const { error } = await supabase
        .from('pta_google_integrations')
        .update({ meet_enabled: enabled })
        .eq('id', integration.id);

      if (error) throw error;

      setIntegration({ ...integration, meet_enabled: enabled });
      toast({
        title: enabled ? 'Enabled' : 'Disabled',
        description: `Google Meet ${enabled ? 'enabled' : 'disabled'}`,
      });
    } catch (error: any) {
      console.error('Error toggling meet:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const toggleDriveEnabled = async (enabled: boolean) => {
    if (!integration?.id) return;

    try {
      const { error } = await supabase
        .from('pta_google_integrations')
        .update({ drive_enabled: enabled })
        .eq('id', integration.id);

      if (error) throw error;

      setIntegration({ ...integration, drive_enabled: enabled });
      toast({
        title: enabled ? 'Enabled' : 'Disabled',
        description: `Google Drive ${enabled ? 'enabled' : 'disabled'}`,
      });
    } catch (error: any) {
      console.error('Error toggling drive:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const syncToCalendar = async (params: {
    action: 'create' | 'update' | 'delete';
    eventType: 'meeting' | 'event';
    eventId: string;
    title: string;
    description?: string;
    startDateTime: string;
    endDateTime?: string;
    location?: string;
    includeGoogleMeet?: boolean;
    googleCalendarEventId?: string;
  }) => {
    // Only sync if calendar integration is enabled
    if (!ptaId || !integration?.is_enabled) {
      return { success: false, skipped: true };
    }

    // Only include Google Meet if Meet integration is also enabled
    const shouldIncludeMeet = params.includeGoogleMeet && integration?.meet_enabled;

    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
        body: { ...params, includeGoogleMeet: shouldIncludeMeet, ptaId },
      });

      if (error) throw error;

      return {
        success: true,
        googleCalendarEventId: data?.googleCalendarEventId,
        googleMeetUrl: data?.googleMeetUrl,
      };
    } catch (error: any) {
      console.error('Calendar sync error:', error);
      return { success: false, error: error.message };
    }
  };

  return {
    integration,
    loading,
    connecting,
    isConnected: !!integration,
    isCalendarEnabled: integration?.is_enabled ?? false,
    isMeetEnabled: integration?.meet_enabled ?? false,
    isDriveEnabled: integration?.drive_enabled ?? false,
    googleEmail: integration?.google_email,
    connectGoogle,
    disconnectGoogle,
    toggleCalendarEnabled,
    toggleMeetEnabled,
    toggleDriveEnabled,
    syncToCalendar,
    refetch: fetchIntegration,
  };
};
